// @ts-nocheck
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Img, { ImagePresentationProps } from "@store/MobiusOverrides/Img/Img";
import SignedOutHeader, { SignedOutHeaderPresentationProps } from "@store/Library/Components/SignedOut/SignedOutHeader";
import { fullViewportHeight } from "@store/Framework/Common/Utilities/siteBanners";
import constants from "@store/constants";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import { pxToRem, pxToRemArray } from "@toolkit/Framework/Common/Utilities/unitHelper";
import parse from "html-react-parser";
import React, { FC } from "react";
import { css } from "styled-components";

const enum fields {
    logo = "logo",
    tag = "tag",
    image = "image",
    title = "title",
    content = "content",
}

export interface MaintenanceLayoutProps extends WidgetProps {
    fields: {
        [fields.logo]: string;
        [fields.tag]: string;
        [fields.image]: string;
        [fields.title]: string;
        [fields.content]: string;
    };
}

export interface MaintenanceLayoutStyles {
    wrapper?: InjectableCss;
    signedOutHeader?: SignedOutHeaderPresentationProps;
    innerWrapper?: InjectableCss;
    cardWrapper?: InjectableCss;
    headerWrapper?: InjectableCss;
    image?: ImagePresentationProps;
    titleWrapper?: InjectableCss;
    tag?: TypographyPresentationProps;
    title?: TypographyPresentationProps;
    contentWrapper?: InjectableCss;
}

export const maintenanceLayoutStyles: MaintenanceLayoutStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            box-sizing: border-box;
            width: 100%;
            min-height: ${fullViewportHeight};
            padding-top: ${pxToRem(24)};
            padding-bottom: ${pxToRem(24)};
            ${x => breakpointValues(x.theme.design.layout.spacing.side, ["padding-left", "padding-right"])}

            ${breakpoint("md")} {
                padding-top: ${pxToRem(45)};
                padding-bottom: ${pxToRem(45)};
            }
        `,
    },
    innerWrapper: {
        css: css`
            display: flex;
            flex: 1 1 auto;
            flex-direction: column;
            justify-content: center;
            ${breakpointValues(pxToRemArray([24, 32, 40]), ["gap"])}
            width: 100%;
            max-width: ${pxToRem(900)};
            margin: 0 auto;
        `,
    },
    cardWrapper: {
        css: css`
            box-sizing: border-box;
            display: flex;
            flex-direction: column;
            gap: ${pxToRem(24)};
            width: 100%;
            border: 1px solid ${getColor("warning.light")};
            border-radius: ${pxToRem(16)};
            background-color: ${getColor("common.background")};
            ${breakpointValues(pxToRemArray([24, 32, 48, 60]), ["padding"])}
        `,
    },
    headerWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: ${pxToRem(16)};

            ${breakpoint("sm")} {
                flex-direction: row;
                align-items: flex-start;
                gap: ${pxToRem(16)};
            }
        `,
    },
    image: {
        css: css`
            flex: 0 0 auto;
            display: block;
            max-width: ${pxToRem(96)};
            object-fit: contain;
        `,
    },
    titleWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: ${pxToRem(8)};
            min-width: 0;
        `,
    },
    tag: {
        css: css`
            color: ${getColor("warning")};
            font-size: ${x => x.theme.design.typography.size.smaller};
            font-weight: ${x => x.theme.design.typography.weight.bold};
            line-height: ${x => x.theme.design.typography.lineHeight.small};
            text-transform: uppercase;
        `,
    },
    title: {
        variant: "h1",
        css: css`
            margin: 0;
        `,
    },
};

const styles = maintenanceLayoutStyles;

/**
 * The full-screen maintenance message shown while the site is in maintenance mode.
 */
const MaintenanceLayout: FC<MaintenanceLayoutProps> = ({ fields: { logo, tag, image, title, content } }) => {
    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="maintenanceLayout">
            <StyledWrapper {...styles.innerWrapper}>
                <SignedOutHeader {...styles.signedOutHeader} logo={logo || undefined} />
                <StyledWrapper {...styles.cardWrapper}>
                    <StyledWrapper {...styles.headerWrapper}>
                        {image && <Img {...styles.image} src={image} altText="" />}
                        <StyledWrapper {...styles.titleWrapper}>
                            {tag && <Typography {...styles.tag}>{tag}</Typography>}
                            {title && <Typography {...styles.title}>{title}</Typography>}
                        </StyledWrapper>
                    </StyledWrapper>
                    {content && (
                        <StyledWrapper {...styles.contentWrapper}>{parse(content, parserOptions)}</StyledWrapper>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: MaintenanceLayout,
    definition: {
        displayName: "Maintenance Layout",
        group: constants.widgetGroup.maintenance,
        allowedContexts: [constants.pageType.maintenance],
        fieldDefinitions: [
            {
                name: fields.logo,
                displayName: "Logo",
                editorTemplate: "ImagePickerField",
                defaultValue: constants.logoPath,
                fieldType: "Contextual",
            },
            {
                name: fields.tag,
                displayName: "Tag",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Contextual",
            },
            {
                name: fields.image,
                displayName: "Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "/userfiles/maintenance-cog.png",
                fieldType: "Contextual",
            },
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Greif+ is temporarily unavailable due to scheduled maintenance.",
                fieldType: "Contextual",
            },
            {
                name: fields.content,
                displayName: "Content",
                editorTemplate: "RichTextField",
                defaultValue:
                    "<p>Greif is currently completing a planned enterprise system upgrade. We apologize for the inconvenience and appreciate your patience while this work is completed.</p><p>If you require assistance during the maintenance window, please contact your <strong>Greif account sales manager or Customer Service representative.</strong></p><p>Thank you for your understanding.</p><p><strong>Greif+ Team</strong></p>",
                fieldType: "Contextual",
            },
        ],
    },
};

export default widgetModule;
